import type { GridColDef } from "@mui/x-data-grid-premium";
import type { ApprovalItemType } from "@entrilia/shared/api/workflowTypes";
import InlineItemsList from "@entrilia/shared/components/InlineItemsList";
import { convertISODateTime } from "@entrilia/shared/utilities/dateUtils";
import type { Approval } from "../../../api/types/approvalTypes";
import { approvalItemTypeCaptionsMap } from "../../../utilities/enumCaptions";
import { getApprovalProgress } from "../../approvals/approvalsHelper";
import ApprovalStatusTag from "../../approvals/ApprovalStatusTag";
import ApprovalItemNameCell from "./ApprovalItemNameCell";

const getActionUsers = (row: Approval): string[] => {
  const actionUsers: string[] = [];

  for (let i = row.approvalStepStates.length - 1; i >= 0; i--) {
    const step = row.approvalStepStates[i];
    const stepRules = step?.rules;
    if (stepRules?.length) {
      for (let j = stepRules.length - 1; j >= 0; j--) {
        const rule = stepRules[j];
        if (rule?.actions?.length) {
          const userName = rule.actions[rule.actions.length - 1]?.user.name;
          if (userName && !actionUsers.includes(userName)) {
            actionUsers.push(userName);
          }
        }
      }
    }
  }

  return actionUsers;
};

export const approvalsGridColumnDefinitions: GridColDef<Approval>[] = [
  {
    field: "itemName",
    headerName: "Requested Item",
    flex: 2,
    minWidth: 200,
    renderCell: ({ row }) => <ApprovalItemNameCell row={row} />,
  },
  {
    field: "itemType",
    headerName: "Type",
    flex: 1,
    minWidth: 160,
    valueGetter: (_value: ApprovalItemType, row: Approval) =>
      row.objectTypeDisplayName ?? approvalItemTypeCaptionsMap[row.itemType],
  },
  {
    field: "createdByUserName",
    headerName: "Requested by",
    flex: 1,
    minWidth: 160,
  },
  {
    field: "createdAt",
    headerName: "Requested at",
    flex: 1,
    minWidth: 160,
    valueFormatter: (value: string) => convertISODateTime(value),
  },
  {
    field: "status",
    headerName: "Status",
    flex: 0.5,
    minWidth: 160,
    renderCell: ({ row }) => <ApprovalStatusTag status={row.status} />,
  },
  {
    field: "approvalStepStates",
    headerName: "Steps",
    flex: 0.5,
    minWidth: 80,
    valueFormatter: (_: string, row: Approval) => getApprovalProgress(row),
  },
  {
    field: "approvalStepStates.actions",
    headerName: "Reviewed by",
    flex: 1,
    minWidth: 160,
    renderCell: ({ row }) => {
      const actionUsers = getActionUsers(row);
      return actionUsers.length > 0 ? (
        <InlineItemsList displayCount={1} items={actionUsers} propGetter={(name) => name} />
      ) : (
        "-"
      );
    },
  },
  {
    field: "lastActionAt",
    headerName: "Reviewed at",
    flex: 1,
    minWidth: 160,
    valueFormatter: (value: string | undefined) => convertISODateTime(value),
  },
];
